import { Button } from '@mui/material';
import { useField, Field } from 'formik';
import { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { useAppDispatch } from '../../lib';
import { openSidebar } from '../../store';
import { Field as FormField, FieldType, EmailInput } from '../../types/form-fields';
import { CheckboxField, FormRow, InputField as FormikInputField } from '../fields';
import { FormBlock } from './common';

export const isEmailInputField = (field: FormField): field is EmailInput => {
  return field.type === FieldType.EmailInput;
};

export const defaultEmailInputFieldProps: EmailInput = {
  type: FieldType.EmailInput,
  placeholder: 'Email',
  required: true,
  name: 'email',
};

type EmailEmailInputFieldSettingsProps = {
  name: string;
  remove?: () => void;
};

const textValueSchema = Yup.string().optional().max(255, 'Should be < 255 characters');

const EmailInputFieldSettings: FC<EmailEmailInputFieldSettingsProps> = ({ name, remove }) => {
  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  const { t } = useTranslation()

  return (
    <EmailInputFieldSettingsWrapper>
      <FormRow>
        <Field
          name={`${name}.placeholder`}
          component={FormikInputField}
          label={t('pages.campaign.checkboxField.placeholder')}
          validate={(value: string) => validate(value, textValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field name={`${name}.required`} component={CheckboxField} label={t('pages.campaign.checkboxField.required')} />
      </FormRow>

      {remove && (
        <Button color="error" onClick={remove}>
          {t('pages.campaign.checkboxField.buttonRemove')}
        </Button>
      )}
    </EmailInputFieldSettingsWrapper>
  );
};

type EmailInputFieldProps = {
  name: string;
  remove?: () => void;
};

export const EmailInputField: FC<EmailInputFieldProps> = ({ name, remove }) => {
  const [field] = useField<EmailInput>(name);
  const dispatch = useAppDispatch();

  const {
    value: { placeholder },
    ...restProps
  } = field;

  const handleClick = () => {
    dispatch(
      openSidebar({
        content: <EmailInputFieldSettings name={name} remove={remove} />,
      })
    );
  };

  return (
    <FormBlock onClick={handleClick}>
      <Cover />

      <StyledInput {...restProps} placeholder={placeholder} />
    </FormBlock>
  );
};

const EmailInputFieldSettingsWrapper = styled.div``;

const Cover = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0 0 0 0;
  cursor: pointer;
`;

const StyledInput = styled.input`
  width: 100%;
  outline: none;
  border-radius: 5px;
  padding: 12px;
  height: 40px;
  font-size: 16px;
  border: 1px solid;
  color: #000;
  background-color: #fff;
  box-sizing: border-box;
`;
